import { useState, useEffect } from 'react';
import { getSubmissions } from '../../services/adminService';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Analytics = () => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getSubmissions();
                setSubmissions(data);
            } catch (error) {
                console.error('Failed to fetch submissions:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Calculate metrics
    const calculateMetrics = () => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        let totalRevenue = 0;
        let monthlyRevenue = 0;
        let weeklyRevenue = 0;
        let paidCount = 0;

        submissions.forEach(sub => {
            // Use the correct field name from Google Sheets
            const amount = parseFloat(sub['Final Amount (₹)'] || sub['Amount Paid'] || sub['amount'] || sub['Amount'] || 0);
            const timestamp = new Date(sub.Timestamp || sub.timestamp);

            if (amount > 0) {
                totalRevenue += amount;
                paidCount++;

                if (timestamp >= startOfMonth) {
                    monthlyRevenue += amount;
                }
                if (timestamp >= sevenDaysAgo) {
                    weeklyRevenue += amount;
                }
            }
        });

        const avgRevenue = paidCount > 0 ? totalRevenue / paidCount : 0;

        return {
            totalRevenue,
            monthlyRevenue,
            weeklyRevenue,
            avgRevenue,
            totalSubmissions: submissions.length,
            paidSubmissions: paidCount
        };
    };

    // Get project type distribution
    const getProjectTypeData = () => {
        const types = {};
        submissions.forEach(sub => {
            const type = sub['Deliverable Type'] || 'Unknown';
            types[type] = (types[type] || 0) + 1;
        });

        return Object.entries(types).map(([name, value]) => ({ name, value }));
    };

    // Get payment status distribution
    const getPaymentStatusData = () => {
        const statuses = {};
        submissions.forEach(sub => {
            const status = sub['Payment Status'] || 'Unknown';
            statuses[status] = (statuses[status] || 0) + 1;
        });

        return Object.entries(statuses).map(([name, value]) => ({ name, value }));
    };

    // Get revenue over time (last 30 days)
    const getRevenueOverTime = () => {
        const days = 30;
        const data = [];
        const now = new Date();

        for (let i = days - 1; i >= 0; i--) {
            const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
            const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

            const dayRevenue = submissions.reduce((sum, sub) => {
                const subDate = new Date(sub.Timestamp || sub.timestamp);
                if (subDate.toDateString() === date.toDateString()) {
                    const amount = parseFloat(sub['Final Amount (₹)'] || sub['Amount Paid'] || sub['amount'] || sub['Amount'] || 0);
                    return sum + amount;
                }
                return sum;
            }, 0);

            data.push({ date: dateStr, revenue: dayRevenue });
        }

        return data;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <span className="material-symbols-outlined animate-spin text-4xl text-white">progress_activity</span>
            </div>
        );
    }

    const metrics = calculateMetrics();
    const projectTypeData = getProjectTypeData();
    const paymentStatusData = getPaymentStatusData();
    const revenueData = getRevenueOverTime();

    const COLORS = ['#ffffff', '#cccccc', '#999999', '#666666', '#333333'];

    return (
        <div className="space-y-8">
            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Total Revenue */}
                <div className="bg-dark-card border border-white/20 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-sm font-medium">Total Revenue</span>
                        <span className="material-symbols-outlined text-white">payments</span>
                    </div>
                    <p className="text-3xl font-bold text-white">₹{metrics.totalRevenue.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">{metrics.paidSubmissions} paid projects</p>
                </div>

                {/* Monthly Revenue */}
                <div className="bg-dark-card border border-white/20 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-sm font-medium">This Month</span>
                        <span className="material-symbols-outlined text-white">calendar_month</span>
                    </div>
                    <p className="text-3xl font-bold text-white">₹{metrics.monthlyRevenue.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">Current month</p>
                </div>

                {/* Weekly Revenue */}
                <div className="bg-dark-card border border-white/20 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-sm font-medium">Last 7 Days</span>
                        <span className="material-symbols-outlined text-white">trending_up</span>
                    </div>
                    <p className="text-3xl font-bold text-white">₹{metrics.weeklyRevenue.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">Past week</p>
                </div>

                {/* Average Project Value */}
                <div className="bg-dark-card border border-white/20 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-sm font-medium">Avg Project</span>
                        <span className="material-symbols-outlined text-white">analytics</span>
                    </div>
                    <p className="text-3xl font-bold text-white">₹{Math.round(metrics.avgRevenue).toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">Per project</p>
                </div>
            </div>

            {/* Submission Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-dark-card border border-white/20 rounded-xl p-6">
                    <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-white text-3xl">description</span>
                        <div>
                            <p className="text-2xl font-bold text-white">{metrics.totalSubmissions}</p>
                            <p className="text-sm text-gray-400">Total Submissions</p>
                        </div>
                    </div>
                </div>

                <div className="bg-dark-card border border-white/20 rounded-xl p-6">
                    <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-white text-3xl">check_circle</span>
                        <div>
                            <p className="text-2xl font-bold text-white">{metrics.paidSubmissions}</p>
                            <p className="text-sm text-gray-400">Paid Projects</p>
                        </div>
                    </div>
                </div>

                <div className="bg-dark-card border border-white/20 rounded-xl p-6">
                    <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-white text-3xl">percent</span>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {metrics.totalSubmissions > 0 ? Math.round((metrics.paidSubmissions / metrics.totalSubmissions) * 100) : 0}%
                            </p>
                            <p className="text-sm text-gray-400">Conversion Rate</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue Over Time */}
                <div className="bg-dark-card border border-white/20 rounded-xl p-6">
                    <h3 className="text-white text-lg font-bold mb-4">Revenue Trend (30 Days)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={revenueData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis dataKey="date" stroke="#999" style={{ fontSize: '12px' }} />
                            <YAxis stroke="#999" style={{ fontSize: '12px' }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                                labelStyle={{ color: '#fff' }}
                            />
                            <Line type="monotone" dataKey="revenue" stroke="#ffffff" strokeWidth={2} dot={{ fill: '#ffffff' }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Project Type Distribution */}
                <div className="bg-dark-card border border-white/20 rounded-xl p-6">
                    <h3 className="text-white text-lg font-bold mb-4">Project Types</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={projectTypeData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {projectTypeData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Payment Status */}
                <div className="bg-dark-card border border-white/20 rounded-xl p-6 lg:col-span-2">
                    <h3 className="text-white text-lg font-bold mb-4">Payment Status</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={paymentStatusData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis dataKey="name" stroke="#999" style={{ fontSize: '12px' }} />
                            <YAxis stroke="#999" style={{ fontSize: '12px' }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                                labelStyle={{ color: '#fff' }}
                            />
                            <Bar dataKey="value" fill="#ffffff" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default Analytics;
